import { useState, useEffect } from 'react';
import { Plane, Building2, Car, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

interface Package {
  id: number;
  sku: string;
  name: string;
  price: number;
  category: string;
  tagline: string;
  city: string;
  duration: string;
  features: string[];
  category_label: string;
  category_icon: string;
}

interface Category {
  key: string;
  label: string;
  icon: string;
  packages: Package[];
}

interface ApiResponse {
  success: boolean;
  categories: Category[];
}

const ICONS: Record<string, React.ElementType> = {
  plane:    Plane,
  building: Building2,
  car:      Car,
  sparkles: Sparkles,
};

function formatNGN(amount: number): string {
  return '₦' + amount.toLocaleString('en-NG');
}

function PackageCard({ pkg }: { pkg: Package }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-white/10 bg-white/[0.02] hover:border-[#C4A77D]/30 transition-all duration-300">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left p-6 flex items-start justify-between gap-4"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.25em] text-[#C4A77D] uppercase mb-1">{pkg.sku}</div>
          <h4 className="text-white font-medium leading-snug mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
            {pkg.name}
          </h4>
          {pkg.tagline && (
            <p className="text-gray-500 text-xs font-light leading-relaxed">{pkg.tagline}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <div className="text-[#C4A77D] text-sm font-medium whitespace-nowrap">
            {formatNGN(pkg.price)}
          </div>
          {open
            ? <ChevronUp className="w-4 h-4 text-gray-500" />
            : <ChevronDown className="w-4 h-4 text-gray-500" />
          }
        </div>
      </button>

      {open && (
        <div className="border-t border-white/5 px-6 pb-6 pt-4">
          <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 text-xs text-gray-400">
            {pkg.city     && <span>📍 {pkg.city}</span>}
            {pkg.duration && <span>⏱ {pkg.duration}</span>}
          </div>
          {pkg.features.length > 0 && (
            <ul className="space-y-1.5">
              {pkg.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-300 font-light">
                  <span className="text-[#C4A77D] mt-0.5 flex-shrink-0">—</span>
                  {f}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-5">
            <a
              href="#contact"
              className="inline-block text-xs tracking-[0.2em] uppercase border border-[#C4A77D]/40 text-[#C4A77D] px-4 py-2 hover:bg-[#C4A77D]/10 transition-colors"
            >
              Enquire
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryTab({
  category,
  active,
  onClick,
}: {
  category: Category;
  active: boolean;
  onClick: () => void;
}) {
  const Icon = ICONS[category.icon] ?? Sparkles;

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-xs tracking-[0.15em] uppercase transition-all border-b-2 ${
        active
          ? 'border-[#C4A77D] text-[#C4A77D]'
          : 'border-transparent text-gray-500 hover:text-gray-300'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {category.label}
    </button>
  );
}

export default function PackagesSection() {
  const [data, setData]         = useState<Category[]>([]);
  const [active, setActive]     = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);

  useEffect(() => {
    fetch('/api/packages')
      .then(r => r.json() as Promise<ApiResponse>)
      .then(json => {
        if (json.success) setData(json.categories);
        else setError(true);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="py-24 text-center">
        <div className="w-6 h-6 border border-[#C4A77D]/40 border-t-[#C4A77D] rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (error || data.length === 0) return null;

  const current = data[active];

  return (
    <section id="packages" className="py-24 md:py-32 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">Service Packages</span>
          <h2 className="text-3xl md:text-5xl mt-4 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Curated Experiences
          </h2>
          <p className="mt-4 text-gray-400 text-sm font-light max-w-xl mx-auto leading-relaxed">
            Pre-negotiated rates with Nigeria's finest hospitality providers. Book once — we handle everything else.
          </p>
        </div>

        {/* Category tabs */}
        <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto">
          {data.map((cat, i) => (
            <CategoryTab
              key={cat.key}
              category={cat}
              active={i === active}
              onClick={() => setActive(i)}
            />
          ))}
        </div>

        {/* Package list for active category */}
        <div className="grid md:grid-cols-2 gap-4">
          {current.packages.map(pkg => (
            <PackageCard key={pkg.id} pkg={pkg} />
          ))}
        </div>
      </div>
    </section>
  );
}
